import baseApi from 'shared/api/baseApi';

import type {
  BulkMortgageRulesPayload,
  BulkMortgageRulesResponse,
  BusinessRuleDraft,
  BusinessRuleFilters,
  BusinessRuleRow,
  BusinessRulesResponse,
} from '../types/businessRule';

type UpdateBusinessRuleBody = {
  id: number;
  businessRule: Partial<Pick<BusinessRuleDraft, 'enabled' | 'value' | 'displayName'>>;
};

const businessRulesApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getBusinessRules: build.query<BusinessRulesResponse, BusinessRuleFilters>({
      query: (filters) => {
        const params = new URLSearchParams();
        if (filters.entityId !== null && filters.entityId !== undefined) {
          params.append('entityId', String(filters.entityId));
        }
        if (filters.entityType) params.append('entityType', filters.entityType);
        if (filters.subEntityId !== null && filters.subEntityId !== undefined) {
          params.append('subEntityId', String(filters.subEntityId));
        }
        if (filters.subEntityType) params.append('subEntityType', filters.subEntityType);
        if (filters.name) params.append('name', filters.name);
        if (filters.names?.length) params.append('names', filters.names.join(','));
        if (filters.entityIds?.length) params.append('entityIds', filters.entityIds.join(','));
        if (filters.enabled !== undefined) params.append('enabled', String(filters.enabled));
        if (filters.page) params.append('page', String(filters.page));
        if (filters.limit) params.append('limit', String(filters.limit));
        if (filters.exact) params.append('exact', 'true');
        return { url: `/admin/businessRules?${params.toString()}`, method: 'GET' as const };
      },
      providesTags: ['BusinessRules'],
    }),
    createBusinessRule: build.mutation<BusinessRuleRow, BusinessRuleDraft>({
      query: (businessRule) => ({
        url: '/admin/businessRules',
        method: 'POST',
        body: businessRule,
      }),
      invalidatesTags: ['BusinessRules'],
    }),
    updateBusinessRule: build.mutation<BusinessRuleRow, UpdateBusinessRuleBody>({
      query: ({ id, businessRule }) => ({
        url: `/admin/businessRules/${id}`,
        method: 'PATCH',
        body: businessRule,
      }),
      invalidatesTags: ['BusinessRules'],
    }),
    // Creates or updates the financing rules over many projects in one transaction.
    bulkUpsertMortgageRules: build.mutation<BulkMortgageRulesResponse, BulkMortgageRulesPayload>({
      query: (body) => ({
        url: '/admin/businessRules/bulk/mortgage',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['BusinessRules'],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetBusinessRulesQuery,
  useCreateBusinessRuleMutation,
  useUpdateBusinessRuleMutation,
  useBulkUpsertMortgageRulesMutation,
} = businessRulesApi;
